import { useCallback, useEffect, useRef } from 'react';
import { v4 as uuid } from 'uuid';

const GLOBAL_UPLOADS = new Set<string>();

export const addUpload = (id: string) => {
  GLOBAL_UPLOADS.add(id);
};

export const removeUpload = (id: string) => {
  GLOBAL_UPLOADS.delete(id);
};

const clearUploads = () => {
  GLOBAL_UPLOADS.clear();
};

export const hasUploadsInProgress = () => GLOBAL_UPLOADS.size > 0;

export const generateUploadId = (): string => `upload-${uuid()}`;

// With a single upload to track, we generate a custom id internally (e.g. StorageSingleImageUpload component)
export const useTrackUpload = () => {
  const uploadIdRef = useRef(generateUploadId());

  const startUploading = useCallback(() => addUpload(uploadIdRef.current), []);
  const stopUploading = useCallback(
    () => removeUpload(uploadIdRef.current),
    [],
  );

  useEffect(() => {
    clearUploads();

    return () => {
      clearUploads();
    };
  }, []);

  return {
    startUploading,
    stopUploading,
  };
};

// With multiple uploads to track, the caller must manage the upload ids themselves
export const useTrackUploads = () => {
  const startUploading = useCallback((id: string) => {
    addUpload(id);
  }, []);

  const stopUploading = useCallback((id: string) => {
    removeUpload(id);
  }, []);

  return {
    startUploading,
    stopUploading,
  };
};
